import { useState, useEffect, useRef } from "react";
import SkillIcon from "./SkillIcon";
import AnimatedText from "./AnimatedText";
import { cn } from "@/lib/utils";

const Skills = () => {
  const [activeSkill, setActiveSkill] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animatingSkill, setAnimatingSkill] = useState<number | null>(null);
  const skillsRef = useRef<HTMLDivElement>(null);
  const autoAnimationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);

          // Start auto-animation for skills when section becomes visible
          if (autoAnimationTimerRef.current) {
            clearTimeout(autoAnimationTimerRef.current);
          }

          // Only animate skills once they've appeared
          if (hasAnimated) {
            startSkillsAnimation();
          } else {
            // Delay the start of skill animations until after they appear
            setTimeout(() => {
              setHasAnimated(true);
              startSkillsAnimation();
            }, 1500);
          }
        } else {
          setIsVisible(false);
          if (autoAnimationTimerRef.current) {
            clearTimeout(autoAnimationTimerRef.current);
          }
        }
      },
      { threshold: 0.2 }
    );

    const skillsSection = document.getElementById("skills");
    if (skillsSection) {
      observer.observe(skillsSection);
    }

    return () => {
      if (skillsSection) {
        observer.unobserve(skillsSection);
      }
      if (autoAnimationTimerRef.current) {
        clearTimeout(autoAnimationTimerRef.current);
      }
    };
  }, [hasAnimated]);

  const startSkillsAnimation = () => {
    let currentIndex = 0;
    const totalSkills = skills.length;

    const animateNextSkill = () => {
      setAnimatingSkill(skills[currentIndex].id);
      currentIndex = (currentIndex + 1) % totalSkills;

      autoAnimationTimerRef.current = setTimeout(() => {
        setAnimatingSkill(null);
        autoAnimationTimerRef.current = setTimeout(animateNextSkill, 300);
      }, 1500);
    };

    animateNextSkill();
  };

  const skills = [
    { id: 1, name: "React", icon: "⚛️" },
    { id: 2, name: "TypeScript", icon: "TS" },
    { id: 3, name: "FastAPI", icon: "🚀" },
    { id: 4, name: "Supabase", icon: "⚡" },
    { id: 5, name: "NextJS", icon: "N" },
    { id: 6, name: "Python", icon: "🐍" },
    { id: 7, name: "Docker", icon: "🐳" },
    { id: 8, name: "GraphQL", icon: "◢" },
    { id: 9, name: "Groq API", icon: "G+" },
    { id: 10, name: "TailwindCSS", icon: "🌊" },
    { id: 11, name: "Node.js", icon: "🟢" },
    { id: 12, name: "MongoDB", icon: "🍃" },
  ];

  const handleSkillHover = (id: number) => {
    setActiveSkill(id);
    setAnimatingSkill(null);

    // Pause auto-animation when user interacts
    if (autoAnimationTimerRef.current) {
      clearTimeout(autoAnimationTimerRef.current);
    }
  };

  const handleSkillLeave = () => {
    setActiveSkill(null);

    // Resume auto-animation after a delay
    if (isVisible) {
      autoAnimationTimerRef.current = setTimeout(startSkillsAnimation, 2000);
    }
  };

  const getRandomTransform = () => {
    const transforms = [
      "rotate(5deg) scale(1.05)",
      "rotate(-5deg) scale(1.05)",
      "translateY(-10px)",
      "scale(1.1)",
      "rotate(10deg)",
      "rotate(-10deg) scale(1.05)",
    ];
    return transforms[Math.floor(Math.random() * transforms.length)];
  };

  return (
    <section id="skills" className="py-20 md:py-32 bg-black overflow-hidden">
      <div className="container mx-auto px-4">
        <div
          className={cn(
            "transform transition-all duration-1000",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
          )}
        >
          <h2 className="font-display text-5xl md:text-6xl tracking-tight mb-12 md:mb-20">
            <AnimatedText text="SKILL STACK" />
          </h2>
        </div>

        <div
          ref={skillsRef}
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6"
        >
          {skills.map((skill, index) => (
            <div
              key={skill.id}
              onMouseEnter={() => handleSkillHover(skill.id)}
              onMouseLeave={handleSkillLeave}
              onClick={() => handleSkillHover(skill.id)}
              className={cn(
                "relative transform transition-all duration-500 cursor-pointer opacity-0 animate-fade-in scale-95 hover:scale-105",
                animatingSkill === skill.id && "z-10"
              )}
              style={{
                transform:
                  animatingSkill === skill.id
                    ? getRandomTransform()
                    : isVisible
                    ? "scale(1)"
                    : "scale(0.95)",
                animationDelay: `${200 + index * 100}ms`,
                transition: "all 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
              }}
            >
              <SkillIcon
                name={skill.name}
                icon={skill.icon}
                isActive={
                  activeSkill === skill.id || animatingSkill === skill.id
                }
                index={index}
                isAnimating={animatingSkill === skill.id}
              />

              {/* Pulse animation behind the skill when it's animating */}
              {animatingSkill === skill.id && (
                <div className="absolute inset-0 -z-10 opacity-50 animate-pulse bg-white rounded-sm transform scale-110"></div>
              )}

              {/* Add a glow effect on hover/active */}
              <div
                className={cn(
                  "absolute inset-0 -z-10 rounded-sm opacity-0 transition-opacity duration-500 bg-gradient-to-tr from-white/30 to-transparent",
                  (activeSkill === skill.id || animatingSkill === skill.id) &&
                    "opacity-100"
                )}
              ></div>
            </div>
          ))}
        </div>

        {/* <div className="mt-20 flex flex-col md:flex-row items-start md:items-center justify-between">
          <p className={cn(
            "max-w-lg text-base mb-8 md:mb-0 transition-all duration-500 delay-500",
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
          )}>
            Gaurav's technical expertise spans the full development stack, from crafting elegant frontend experiences to architecting robust backend systems. He specializes in creating high-performance, scalable applications with clean, maintainable code.
          </p>
          
          <a 
            href="/resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-block border border-white px-8 py-3 uppercase text-sm tracking-widest transition-all duration-700 delay-700 relative overflow-hidden group",
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
            )}
          >
            <span className="relative z-10 group-hover:text-black transition-colors duration-300">Download Resume</span>
            <span className="absolute inset-0 bg-white transform scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300"></span>
          </a>
        </div> */}
      </div>
    </section>
  );
};

export default Skills;
